import { isBarLabeled } from '@/utils/chartAxis';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface BarAxisPoint {
  axisLabel: string;
}

interface BarAxisLabelsProps {
  points: BarAxisPoint[];
  active: number | null;
  /** Must match the gap used between the bars so labels stay centered under them. */
  gapClass?: string;
  /** Soft cap on how many labels to show before thinning kicks in. */
  maxLabels?: number;
  /** Optional: indices to show on mobile instead of default isBarLabeled logic. */
  mobileShow?: Set<number>;
}

/**
 * X-axis labels for the bar charts. Unlike the curve labels these sit inside
 * flex cells so they line up with the bars, but the first and last ones are
 * pinned to the edges (with a small inset) so they never run off the chart.
 * When there are more months than fit comfortably the labels are thinned out,
 * while the first and last month always stay labelled.
 */
export default function BarAxisLabels({
  points,
  active,
  gapClass = 'gap-1.5 md:gap-2',
  maxLabels = 12,
  mobileShow,
}: BarAxisLabelsProps) {
  const n = points.length;
  const narrow = useMediaQuery('(max-width: 640px)');

  return (
    <div className={`flex ${gapClass}`}>
      {points.map((point, i) => {
        const isFirst = i === 0;
        const isLast = i === n - 1;
        const show = narrow && mobileShow ? mobileShow.has(i) : isBarLabeled(i, n, maxLabels);
        const alignClass = isFirst ? 'pl-0.5 text-left' : isLast ? 'pr-0.5 text-right' : 'text-center';
        const colorClass = active === i ? 'text-foreground-900' : 'text-foreground-500';

        return (
          <span
            key={`${point.axisLabel}-${i}`}
            className={`flex-1 whitespace-nowrap text-[10px] font-medium md:text-xs ${alignClass} ${colorClass}`}
          >
            {show ? point.axisLabel : ''}
          </span>
        );
      })}
    </div>
  );
}